import React, { useEffect, useState } from 'react';

import { Button, message, Space, Typography } from 'antd';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';

import Filter from './Filter';
import apiConfig from '../../../API_Utils/APIUtil/ApiConfig';
import CallApi from '../../../API_Utils/APIUtil/CallApi';
import { getError, validator } from '../../../clientUtils';
import { FILE_TYPE_ATTRIBUTE } from '../../../constants';
import AsyncBulkUpload from '../../BulkUploaders/AsyncBulkUpload';
import {
  JOB_TYPES,
  POLLING_FREQ,
  UI_JOB_STATUS,
  JOB_STATUS_MAPPING,
  getCTAText,
  ifButtonDisabled,
  getJobType,
  TP_JOB_NAMES
} from '../../BulkUploaders/BulkUploaderUtils';
import BULK_UPLOADER_TITLE from '../../BulkUploaders/constants';
import ROInfoMessage from '../../BulkUploaders/ROInfoMessage';

const { Title } = Typography;

const onError = (error) => message.error(getError(error));

const sanitizeFilters = (filters) => filters.filter((item) => item.name !== 'buyerId' && !!item.option);

const getFilter = (f) => {
  if (typeof f !== 'object' || f === null) {
    throw new Error("Invalid input: 'f' must be a non-null object.");
  }

  return Object.keys(f).map((filter) => ({
    name: filter,
    option: Array.isArray(f[filter]?.enum)
      ? f[filter].enum.map((val) => ({
          value: val?.value || val,
          label: val?.label || val
        }))
      : null
  }));
};

const onUpload = (jobName, file, onSuccess) => {
  const params = { jobName };

  CallApi(
    apiConfig.job.getPresignedURL,
    params,
    null,
    undefined,
    async (res) => {
      const headers = { 'Content-Type': file.type };

      try {
        const response = await axios.put(res, file, { headers });
        if (response.status === 200) onSuccess(file, res);
      } catch {
        message.error('Failed to upload file');
      }
    },
    onError
  );
};

const getBody = (action, jobType, fileLink, validationJobId, metaData) => {
  const fileType = `BULK_TP_${TP_JOB_NAMES[action]}_${jobType === JOB_TYPES.validation ? 'INPUT_FILE' : 'SUBMIT_FILE'}`;
  const baseJobName = `POSI_TP_${TP_JOB_NAMES[action]}_`;

  if (jobType === JOB_TYPES.validation) {
    return {
      job_name: `${baseJobName}VALIDATE`,
      files: [
        {
          type: fileType,
          file_link: fileLink
        }
      ],
      metadata: metaData
    };
  }

  return {
    job_name: `${baseJobName}SUBMIT`,
    validate_job_id: validationJobId
  };
};

const AsyncTpUploader = () => {
  const { id, type, action } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [sampleDownloadLink, setSampleDownloadLink] = useState(null);
  const [files, setFiles] = useState([]);
  const [jobStatus, setJobStatus] = useState(UI_JOB_STATUS.uploading);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [filters, setFilters] = useState(null);
  const [allValues, setAllValues] = useState({});
  const [fileUploadManagerId, setFileUploadManagerId] = useState('');

  useEffect(() => {
    setFiles([]);
    setJobStatus(UI_JOB_STATUS.uploading);
    CallApi(
      apiConfig.masters.tp.searchSchema,
      undefined,
      setLoading,
      undefined,
      (res) => {
        setFilters(sanitizeFilters(getFilter(res.properties)));
        setAllValues({ saleChannel: 'DOMESTIC_OFFLINE' });

        const params = { saleChannel: 'DOMESTIC_OFFLINE', file_type: TP_JOB_NAMES[action] };
        CallApi(apiConfig.masters.tp.sample, params, undefined, setLoading, (r) => setSampleDownloadLink(r), onError);
      },
      onError
    );
  }, []);

  useEffect(() => {
    let pollingId;
    if (id) {
      const getJobStatus = () => {
        CallApi(
          { ...apiConfig.job.status, path: apiConfig.job.status.path(id) },
          null,
          null,
          null,
          (response) => {
            const stopPolling = pollingId && JOB_STATUS_MAPPING[response.status] === 'end';
            if (stopPolling) clearInterval(pollingId);

            const file = response?.files?.find((file) => file.type.includes(TP_JOB_NAMES[action]));
            setFiles([{ ...file, summary: file?.validationSummary, link: file?.fileLink }]);
            setFileUploadManagerId(response?.metadata?.FileUploadManagerId);
            setJobStatus(getJobType(response.status, response.job_name, [file?.validationSummary]));
          },
          onError
        );
      };

      getJobStatus();

      pollingId = setInterval(getJobStatus, POLLING_FREQ);
    }
    return () => clearInterval(pollingId);
  }, [navigate, type, id]);

  const uploadProps = {
    title: 'Transfer Pricing',
    description: 'Please upload the list of Transfer Pricing (in CSV format with max file size of 5 MB)',
    sampleDownloadText: 'Download Sample file',
    sampleDownloadLink,
    accept: FILE_TYPE_ATTRIBUTE.csv,
    files,
    validator,
    onUpload: (file) => {
      onUpload(`POSI_TP_${TP_JOB_NAMES[action]}_VALIDATE`, file, (f, link) => {
        setFiles([{ ...f, fileLink: link }]);
        setJobStatus(UI_JOB_STATUS.uploaded);
      });
    },
    onRemove: () => {
      setFiles([]);
      setJobStatus(UI_JOB_STATUS.uploading);
      message.success('File deleted successfully!');
    },
    deleteDisabled: [UI_JOB_STATUS.submitted, UI_JOB_STATUS.submitting, UI_JOB_STATUS.validating].includes(jobStatus)
  };

  const fileUploaderProps = {
    message: '',
    items: [uploadProps]
  };

  const handleClick = () => {
    if (jobStatus === UI_JOB_STATUS.uploaded) {
      CallApi(
        apiConfig.job.validate,
        null,
        getBody(action, JOB_TYPES.validation, files?.[0]?.fileLink, undefined, allValues),
        setButtonLoading,
        (res) => navigate(`/masters/transfer-pricing/bulk/${action}/${res.job_id}`),
        onError
      );
      return;
    }

    const path = {
      ...apiConfig.masters.tp.sendForApproval,
      path: apiConfig.masters.tp.sendForApproval.path(fileUploadManagerId)
    };
    CallApi(
      path,
      null,
      null,
      setButtonLoading,
      () => {
        message.success('Job submitted');
        navigate(`/masters/transfer_pricing`);
      },
      onError
    );
  };

  const onFiltersChange = (value) => {
    const params = { saleChannel: value, file_type: TP_JOB_NAMES[action] };

    CallApi(apiConfig.masters.tp.sample, params, undefined, setLoading, (r) => setSampleDownloadLink(r), onError);
  };

  const filtersView = (
    <Space wrap>
      {filters?.map((filter) => {
        const isSaleChannel = filter?.name === 'saleChannel';

        if (isSaleChannel) {
          return (
            <Filter
              key="filter-1"
              allValues={allValues}
              onChange={onFiltersChange}
              options={filter}
              setAllValues={setAllValues}
            />
          );
        }
        return <Filter key="filter-2" allValues={allValues} options={filter} setAllValues={setAllValues} />;
      })}
    </Space>
  );

  const titleAndFiltersView = (
    <Space direction="vertical">
      <Title level={1}>{BULK_UPLOADER_TITLE.SKUTP}</Title>
      {filtersView}
    </Space>
  );

  return (
    <AsyncBulkUpload
      button={
        <Button
          disabled={
            ifButtonDisabled(jobStatus, files?.validationSummary) || Object.keys(allValues).length !== filters?.length
          }
          loading={buttonLoading}
          onClick={handleClick}
          type="primary">
          {getCTAText(jobStatus)}
        </Button>
      }
      group={['masters']}
      info={<ROInfoMessage status={jobStatus} />}
      loading={loading}
      menuKey="transfer_pricing"
      title={titleAndFiltersView}
      uploadersProp={[fileUploaderProps]}
    />
  );
};

export default AsyncTpUploader;
